import { useEffect, type JSX } from "react";
import { useLocation } from "react-router";
import { startNewProject } from "./ChatSlice_Store";
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import {
  selectCurrentProjectId,
  fetchUserProjects,
  selectUserProjects,
  selectUserProjectsLoading
} from "./ProjectSlice_Store";

// Import child components
import LayoutMainColumn from "./components/layout/LayoutMainColumn";
import LayoutSidebars from "./components/layout/LayoutSidebars";
import WelcomeModal from "./components/WelcomeModal_Component";
import {
  useMobileSidebar,
  useSidebarCollapse,
  useTabletKeyboardStyle,
  useWelcomeModal,
} from "./hooks/useLayoutChrome";
import {
  useChatInitialization,
  useProjectSwitching,
  useProjectUrlSync,
} from "./hooks/useProjectSwitching";

export default function MainLayout_Component(): JSX.Element {
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Admin pages share the shell but none of the project chrome
  const isAdminRoute = location.pathname.startsWith('/admin');

  const mobileSidebar = useMobileSidebar();
  const { isCollapsed, setCollapsed } = useSidebarCollapse();
  const welcomeModal = useWelcomeModal();
  const tabletKeyboardStyle = useTabletKeyboardStyle();

  // Get user authentication state
  const isAuthenticated = useAppSelector(state => state.userAuthAndAdminSlice?.isAuthenticated);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const userProjects = useAppSelector(selectUserProjects);
  const userProjectsLoading = useAppSelector(selectUserProjectsLoading);

  const handleProjectClick = useProjectSwitching(currentProjectId);
  useChatInitialization(isAdminRoute, currentProjectId);
  useProjectUrlSync(isAdminRoute, currentProjectId);

  // Fetch user projects when authenticated
  // Skip on admin routes - admin pages don't need project list
  // Note: Username no longer required - derived from bearer token by APIM
  useEffect(() => {
    if (!isAdminRoute && isAuthenticated) {
      void dispatch(fetchUserProjects());
    }
  }, [isAuthenticated, dispatch, isAdminRoute]);

  const handleStartProject = (): void => {
    console.log('🆕 [NEW CHAT] Button clicked - starting new project flow');
    void dispatch(startNewProject());
  };

  return (
    <div
      className="h-screen tablet:h-[100dvh] flex overflow-hidden"
      style={{ backgroundColor: "#F9F8F3", ...tabletKeyboardStyle }}
    >
      {!isAdminRoute && (
        <LayoutSidebars
          isCollapsed={isCollapsed}
          onCollapseToggle={setCollapsed}
          onStartProject={handleStartProject}
          projects={userProjects}
          currentProjectId={currentProjectId}
          onProjectClick={(projectId) => void handleProjectClick(projectId)}
          projectsLoading={userProjectsLoading}
          isMobileSidebarOpen={mobileSidebar.isOpen}
          onCloseMobileSidebar={mobileSidebar.close}
        />
      )}

      <LayoutMainColumn
        isMobileSidebarOpen={mobileSidebar.isOpen}
        onToggleSidebar={mobileSidebar.toggle}
        onOpenWelcomeModal={!isAdminRoute ? welcomeModal.open : undefined}
      />

      {!isAdminRoute && (
        <WelcomeModal isOpen={welcomeModal.isOpen} onClose={welcomeModal.dismiss} />
      )}
    </div>
  );
}
